import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import api, { errorMessage, API } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { ArrowLeft, Loader2, FileDown, Link2, RefreshCw, Ban, Copy, CheckCircle2, FileSpreadsheet, MessageCircle } from "lucide-react";
import { toast } from "sonner";
import { formatRupiah, formatTanggal } from "../lib/format";

export default function InvoiceDetailPage() {
    const { id } = useParams();
    const nav = useNavigate();
    const { user } = useAuth();
    const isAdmin = user?.role === "admin";
    const [inv, setInv] = useState(null);
    const [loading, setLoading] = useState(true);
    const [copied, setCopied] = useState(false);

    const load = async () => {
        setLoading(true);
        try { const { data } = await api.get(`/invoice/${id}`); setInv(data); }
        catch (e) { toast.error(errorMessage(e)); nav("/invoice"); }
        finally { setLoading(false); }
    };

    useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

    const downloadPDF = async () => {
        try {
            const res = await api.get(`/invoice/${id}/pdf`, { responseType: "blob" });
            const url = URL.createObjectURL(res.data);
            const a = document.createElement("a");
            a.href = url; a.download = `Invoice_${inv.nomor_invoice}.pdf`; a.click();
            URL.revokeObjectURL(url);
        } catch (e) { toast.error("Gagal download PDF"); }
    };

    const downloadExcel = async () => {
        try {
            const params = new URLSearchParams({
                vendor_id: inv.vendor_id,
                tanggal_mulai: inv.tanggal_mulai,
                tanggal_akhir: inv.tanggal_akhir,
                jenis_transaksi: "all",
            });
            const res = await api.get(`/export/excel?${params.toString()}`, { responseType: "blob" });
            const url = URL.createObjectURL(res.data);
            const a = document.createElement("a");
            const safeVendor = (inv.nama_vendor_snapshot || "vendor").replace(/[^\w-]+/g, "_");
            a.href = url; a.download = `Invoice_${inv.nomor_invoice}_${safeVendor}.xlsx`; a.click();
            URL.revokeObjectURL(url);
            toast.success("Excel berhasil diunduh");
        } catch (e) { toast.error(errorMessage(e) || "Gagal download Excel"); }
    };

    const waNumber = (raw) => {
        if (!raw) return "";
        const n = String(raw).replace(/[^\d]/g, "");
        if (n.startsWith("62")) return n;
        if (n.startsWith("0")) return "62" + n.slice(1);
        return "62" + n;
    };

    const sendWA = () => {
        if (!inv.no_hp_snapshot) return toast.error("Vendor tidak punya no HP di data snapshot");
        if (!publicUrl) return toast.error("Link publik dibatalkan. Regenerate dulu untuk kirim ke WA.");
        const num = waNumber(inv.no_hp_snapshot);
        const perusahaan = inv.pengaturan_snapshot?.nama_perusahaan || "CV. MERANGGI JAYA GARMENT";
        const msg = [
            `Halo, invoice periode ${formatTanggal(inv.tanggal_mulai)} - ${formatTanggal(inv.tanggal_akhir)} sudah kami tutup.`,
            `Nomor: ${inv.nomor_invoice}`,
            `Total penghasilan: ${formatRupiah(inv.total_penghasilan_vendor)}`,
            `Silakan lihat detail: ${publicUrl}`,
            ``,
            `Terima kasih.`,
            `- ${perusahaan}`,
        ].join("\n");
        window.open(`https://wa.me/${num}?text=${encodeURIComponent(msg)}`, "_blank");
    };

    const publicUrl = inv?.public_token
        ? `${window.location.origin}/public/invoice/${inv.public_token}`
        : null;

    const copyLink = async () => {
        if (!publicUrl) return;
        try { await navigator.clipboard.writeText(publicUrl); setCopied(true); toast.success("Link disalin"); setTimeout(() => setCopied(false), 2000); }
        catch { toast.error("Gagal salin link"); }
    };

    const regenerateToken = async () => {
        try { await api.post(`/invoice/${id}/regenerate-token`, {}); toast.success("Link publik baru dibuat"); load(); }
        catch (e) { toast.error(errorMessage(e)); }
    };
    const revokeToken = async () => {
        try { await api.post(`/invoice/${id}/revoke-token`, {}); toast.success("Link publik dibatalkan"); load(); }
        catch (e) { toast.error(errorMessage(e)); }
    };

    if (loading || !inv) return <div className="py-16 text-center"><Loader2 className="w-6 h-6 text-teal-600 animate-spin mx-auto" /></div>;

    const totalPeng = inv.total_bon_kain + inv.total_bon_peralatan + inv.total_kasbon + inv.total_potongan_reject;
    const totalPem = inv.total_setor + inv.total_kredit_reject_selesai + inv.saldo_awal;
    const tp = Number(inv.total_penghasilan_vendor);
    const pengaturan = inv.pengaturan_snapshot || {};

    return (
        <div className="space-y-5">
            <div className="flex items-center justify-between gap-3 flex-wrap">
                <div className="flex items-center gap-3">
                    <button onClick={() => nav("/invoice")} className="p-2 rounded-xl hover:bg-slate-100 text-slate-600" data-testid="invoice-back-btn"><ArrowLeft className="w-4 h-4" /></button>
                    <div>
                        <h2 className="text-2xl sm:text-3xl font-extrabold text-slate-900 tracking-tight">{inv.nomor_invoice}</h2>
                        <p className="text-sm text-slate-500 mt-1">Ditutup pada {formatTanggal(inv.tanggal_tutup)} oleh {inv.ditutup_oleh}</p>
                    </div>
                </div>
                <div className="flex items-center gap-2 flex-wrap">
                    <Button onClick={downloadPDF} data-testid="invoice-pdf-btn" variant="outline" className="rounded-xl gap-2"><FileDown className="w-4 h-4" /> PDF</Button>
                    {isAdmin && <Button onClick={downloadExcel} data-testid="invoice-excel-btn" variant="outline" className="rounded-xl gap-2"><FileSpreadsheet className="w-4 h-4" /> Excel</Button>}
                    {inv.public_token && inv.no_hp_snapshot && (
                        <Button onClick={sendWA} data-testid="invoice-wa-btn" className="rounded-xl gap-2 bg-emerald-600 hover:bg-emerald-700 text-white"><MessageCircle className="w-4 h-4" /> Kirim ke WA</Button>
                    )}
                </div>
            </div>

            {inv.public_token && (
                <div data-testid="invoice-public-link-card" className="glass-card rounded-2xl p-4 border border-teal-100/70">
                    <div className="flex items-start justify-between gap-3 flex-wrap">
                        <div className="min-w-0 flex-1">
                            <div className="text-xs font-semibold uppercase tracking-wider text-teal-700 mb-1 flex items-center gap-2"><Link2 className="w-3.5 h-3.5" /> Link Publik Vendor</div>
                            <div className="text-xs text-slate-600 mb-2">Bagikan link ini ke vendor supaya bisa akses invoice tanpa login.</div>
                            <div className="text-xs font-mono bg-slate-50 border border-slate-200 rounded-lg px-2.5 py-1.5 break-all">{publicUrl}</div>
                        </div>
                        <div className="flex items-center gap-2 flex-shrink-0">
                            <Button onClick={copyLink} size="sm" variant="outline" data-testid="invoice-copy-link-btn" className="rounded-xl gap-1.5">{copied ? <CheckCircle2 className="w-3.5 h-3.5 text-emerald-600" /> : <Copy className="w-3.5 h-3.5" />} Salin</Button>
                            {isAdmin && <Button onClick={regenerateToken} size="sm" variant="outline" data-testid="invoice-regen-link-btn" className="rounded-xl gap-1.5"><RefreshCw className="w-3.5 h-3.5" /> Regenerate</Button>}
                            {isAdmin && <Button onClick={revokeToken} size="sm" variant="outline" data-testid="invoice-revoke-link-btn" className="rounded-xl gap-1.5 text-rose-600"><Ban className="w-3.5 h-3.5" /> Cabut</Button>}
                        </div>
                    </div>
                </div>
            )}

            {/* Invoice paper */}
            <div className="glass-card rounded-2xl p-6 sm:p-8 space-y-6">
                <div className="pb-4 border-b border-slate-200">
                    <div className="text-xl font-extrabold text-teal-700">{pengaturan.nama_perusahaan || "CV. MERANGGI JAYA GARMENT"}</div>
                    {pengaturan.alamat && <div className="text-xs text-slate-600">{pengaturan.alamat}</div>}
                    <div className="text-xs text-slate-600">{[pengaturan.no_telp, pengaturan.email].filter(Boolean).join(" · ")}</div>
                </div>

                <div>
                    <div className="text-sm font-bold text-slate-900 tracking-wide mb-3">INVOICE VENDOR · {inv.nomor_invoice}</div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-2 text-sm">
                        <div className="flex gap-3"><span className="text-slate-500 w-20 flex-shrink-0">Vendor</span><span className="font-semibold text-slate-900">{inv.nama_vendor_snapshot} ({inv.kode_vendor_snapshot})</span></div>
                        <div className="flex gap-3"><span className="text-slate-500 w-24 flex-shrink-0">Tanggal Tutup</span><span className="font-semibold text-slate-900">{formatTanggal(inv.tanggal_tutup)}</span></div>
                        <div className="flex gap-3"><span className="text-slate-500 w-20 flex-shrink-0">No HP</span><span className="text-slate-800">{inv.no_hp_snapshot || "-"}</span></div>
                        <div className="flex gap-3"><span className="text-slate-500 w-24 flex-shrink-0">Periode</span><span className="text-slate-800">{formatTanggal(inv.tanggal_mulai)} — {formatTanggal(inv.tanggal_akhir)}</span></div>
                        <div className="flex gap-3"><span className="text-slate-500 w-20 flex-shrink-0">Rekening</span><span className="text-slate-800">{inv.nama_bank_snapshot} {inv.no_rekening_snapshot}</span></div>
                        <div className="flex gap-3"><span className="text-slate-500 w-24 flex-shrink-0">Status</span><span className="text-[10px] font-semibold uppercase px-2 py-1 rounded-md border bg-emerald-50 text-emerald-700 border-emerald-200">TERTUTUP</span></div>
                    </div>
                </div>

                <div>
                    <div className="bg-teal-600 text-white text-xs font-bold uppercase tracking-wider px-3 py-2 rounded-t-lg">Pengeluaran Vendor</div>
                    <div className="border border-t-0 border-slate-200 rounded-b-lg">
                        {[
                            ["Bon Kain", inv.total_bon_kain, inv.rincian.bon_kain.length],
                            ["Bon Peralatan", inv.total_bon_peralatan, inv.rincian.bon_peralatan.length],
                            ["Kasbon", inv.total_kasbon, inv.rincian.kasbon.length],
                            ["Potongan Reject (Permanen + Expired)", inv.total_potongan_reject, null],
                        ].map(([label, val, count]) => (
                            <div key={label} className="flex justify-between items-center px-3 py-2 text-sm border-b border-slate-100 last:border-b-0">
                                <span className="text-slate-700">{label}{count !== null && <span className="text-slate-400 text-xs ml-1">({count} tx)</span>}</span>
                                <span className="font-semibold num-tabular text-slate-900">{formatRupiah(val)}</span>
                            </div>
                        ))}
                        <div className="flex justify-between items-center px-3 py-2.5 bg-slate-50 rounded-b-lg">
                            <span className="text-sm font-bold text-slate-900">SUBTOTAL PENGELUARAN</span>
                            <span className="font-bold num-tabular text-rose-700">{formatRupiah(totalPeng)}</span>
                        </div>
                    </div>
                </div>

                <div>
                    <div className="bg-teal-600 text-white text-xs font-bold uppercase tracking-wider px-3 py-2 rounded-t-lg">Pemasukan Vendor</div>
                    <div className="border border-t-0 border-slate-200 rounded-b-lg">
                        {[
                            ["Setor Barang Jadi", inv.total_setor, inv.rincian.setor_barang.length],
                            ["Kredit Reject Selesai Diperbaiki", inv.total_kredit_reject_selesai, inv.rincian.reject_selesai_kredit.length],
                            ["Saldo Awal (periode sebelumnya)", inv.saldo_awal, null],
                        ].map(([label, val, count]) => (
                            <div key={label} className="flex justify-between items-center px-3 py-2 text-sm border-b border-slate-100 last:border-b-0">
                                <span className="text-slate-700">{label}{count !== null && <span className="text-slate-400 text-xs ml-1">({count} tx)</span>}</span>
                                <span className={`font-semibold num-tabular ${val < 0 ? "text-rose-700" : "text-slate-900"}`}>{formatRupiah(val)}</span>
                            </div>
                        ))}
                        <div className="flex justify-between items-center px-3 py-2.5 bg-slate-50 rounded-b-lg">
                            <span className="text-sm font-bold text-slate-900">SUBTOTAL PEMASUKAN</span>
                            <span className="font-bold num-tabular text-emerald-700">{formatRupiah(totalPem)}</span>
                        </div>
                    </div>
                </div>

                <div data-testid="invoice-detail-total" className={`p-5 rounded-xl ${tp >= 0 ? "bg-emerald-50 border-2 border-emerald-300" : "bg-rose-50 border-2 border-rose-300"}`}>
                    <div className="flex justify-between items-center">
                        <span className="text-base font-bold uppercase tracking-wider text-slate-900">Penghasilan Vendor</span>
                        <span className={`text-3xl font-extrabold num-tabular ${tp >= 0 ? "text-emerald-700" : "text-rose-700"}`}>{formatRupiah(tp)}</span>
                    </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 pt-8">
                    {["Dibuat oleh", "Dicek oleh", "Disetujui oleh"].map((label, idx) => {
                        const nama = [inv.dibuat_oleh, inv.dicek_oleh, inv.disetujui_oleh][idx];
                        return (
                            <div key={label} className="text-center">
                                <div className="text-xs text-slate-500 mb-8">{label}</div>
                                <div className="border-t border-slate-400 pt-2 text-sm font-semibold text-slate-900">{nama}</div>
                            </div>
                        );
                    })}
                </div>

                {inv.catatan_admin && <div className="pt-4 border-t border-slate-200 text-xs text-slate-500 italic">Catatan: {inv.catatan_admin}</div>}
            </div>
        </div>
    );
}
